// src/pages/webmaster/AppointmentsTable.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../../context/AuthProvider";
import Modal from "react-modal";
import ExportSchedule from "../../components/ExportSchedule";
import API_BASE_URL from "../../config/apiConfig";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import PatientForm, { CreatedPatient } from "../../components/PatientForm";
import copy from "../../assets/images/copy.svg";
import avatar from "../../assets/images/icon-avatar.svg";
import setting from "../../assets/images/setting.svg";
import trash from "../../assets/images/trash.svg";
import edit from "../../assets/images/edit-pen.svg";
import clock from "../../assets/images/clock.svg";
import plusCircle from "../../assets/images/plus-circle.svg";
import phone from "../../assets/images/phone.svg";
import StatusDropdown, { Status } from "../../components/StatusDropdown";
import AppointmentHistoryModal from "../../components/AppointmentHistoryModal";
import Swal from "sweetalert2";

// React DnD
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDragDropManager } from "react-dnd";

Modal.setAppElement("#root");

const ItemTypes = { APPOINTMENT: "appointment" } as const;

interface TimeInterval {
  start: string;
  end: string;
}

export interface AppointmentProps {
  id: string;
  therapist_id: string;
  patient_id: string;
  type: "therapy" | "activity" | "medical_activity" | "medical_consult";
  appointment_description: string;
  status?: string;
  start_time: string;
  end_time: string;
  patient_name: string;
}

interface AppointmentCellProps {
  appointments: AppointmentProps[];
  therapistId: string;
  interval: TimeInterval;
  selectedDate: string;
  onAddAppointment: (therapistId: string, interval: TimeInterval) => void;
  onDropAppointment: (
    draggedAppointment: AppointmentProps,
    newTherapistId: string,
    newInterval: TimeInterval
  ) => void;
  getTherapyTypeValue: (key: string) => string;
  getActivityValue: (key: string) => string;
  getMedActivityValue: (key: string) => string;
  getMedConsultValue: (key: string) => string;
  deleteAppointment: (id: string) => void;
  getTherapyShortName: (key: string) => string;
  getActivityShortName: (key: string) => string;
  getMedActivityShortName: (key: string) => string;
  getMedConsultShortName: (key: string) => string;
  viewMode: "scroll" | "full";
  statuses: Status[];
  onStatusChange: (appointmentId: string, newStatus: string) => void;
  onEditAppointment: (appointment: AppointmentProps) => void;
  cellBackground?: string;
  onOpenHistory: (id: string) => void;
  canUseCell: (therapistId: string, interval: TimeInterval) => boolean;
  isMedicId: (id: string) => boolean;
}

export interface Therapist {
  user_id: string;
  firstname: string;
  lastname: string;
  email: string;
  is_active: boolean;
  therapist_category?: string | null;
  order_in_category?: number | null;
}

export interface Medic {
  user_id: string;
  firstname: string;
  lastname: string;
  email: string;
  is_active: boolean;
}

export interface TherapistCategory {
  key: string;
  value: string;
  priority: string;
  color: string;
}

function hexToRGBA(hex: string, opacity: number): string {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const isSameISO = (iso: string, date: string, hhmm: string) =>
  iso === `${date}T${hhmm}:00`;

/* -------------------- DRAGGABLE APPOINTMENT -------------------- */
const DraggableAppointment: React.FC<{
  appointment: AppointmentProps;
  statuses: Status[];
  getTherapyTypeValue: (key: string) => string;
  getActivityValue: (key: string) => string;
  getMedActivityValue: (key: string) => string;
  getMedConsultValue: (key: string) => string;
  onStatusChange: (id: string, newStatus: string) => void;
  deleteAppointment: (id: string) => void;
  getTherapyShortName: (key: string) => string;
  getActivityShortName: (key: string) => string;
  getMedActivityShortName: (key: string) => string;
  getMedConsultShortName: (key: string) => string;
  viewMode: "scroll" | "full";
  onEdit: (appointment: AppointmentProps) => void;
  onOpenHistory: (id: string) => void;
}> = ({
  appointment,
  statuses,
  getTherapyTypeValue,
  getActivityValue,
  getMedActivityValue,
  getMedConsultValue,
  onStatusChange,
  deleteAppointment,
  onEdit,
  getTherapyShortName,
  getActivityShortName,
  getMedActivityShortName,
  getMedConsultShortName,
  viewMode,
  onOpenHistory,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.APPOINTMENT,
    item: appointment,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const isActivity = appointment.type === "activity";
  const isTherapy = appointment.type === "therapy";
  const isMedActivity = appointment.type === "medical_activity";
  const isMedConsult = appointment.type === "medical_consult";

  const filteredStatuses = React.useMemo(() => {
    if (isActivity) {
      const list = statuses.filter(
        (st) =>
          st.type === "activity" &&
          st.activity_key === appointment.appointment_description
      );
      // fallback (dacă nu avem statusuri per-activitate)
      if (list.length > 0) return list;
      return statuses.filter((st) => st.type === "activity");
    }

    if (isTherapy) {
      return statuses.filter((st) => st.type === "therapy");
    }

    if (isMedActivity) {
      let list =
        statuses.filter((st) => st.type === "medical_activity") ||
        [];
      if (list.length === 0)
        list = statuses.filter((st) => st.type === "medical");
      if (list.length === 0)
        list = statuses.filter((st) => st.type === "activity");
      if (list.length === 0)
        list = statuses.filter(
          (st) => !st.type || st.type === "therapy" || st.type === "activity"
        );
      return list;
    }

    // medical_consult
    let list =
      statuses.filter((st) => st.type === "medical_consult") || [];
    if (list.length === 0)
      list = statuses.filter((st) => st.type === "medical");
    if (list.length === 0)
      list = statuses.filter((st) => st.type === "therapy");
    if (list.length === 0)
      list = statuses.filter(
        (st) => !st.type || st.type === "therapy" || st.type === "activity"
      );
    return list;
  }, [statuses, appointment, isActivity, isTherapy, isMedActivity, isMedConsult]);

  const divRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        divRef.current &&
        !divRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { token } = useAuth();

  const normalizeSnapshot = (snap: any): any | null => {
    if (!snap) return null;
    if (typeof snap === "object") return snap;
    if (typeof snap === "string") {
      try {
        return JSON.parse(snap);
      } catch {
        return null;
      }
    }
    return null;
  };

  const fetchLastAuditForAppointment = async (appointmentId: string) => {
    try {
      const r = await fetch(
        `${API_BASE_URL}/admin/appointments/audit?appointment_id=${encodeURIComponent(
          String(appointmentId)
        )}&limit=20&offset=0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await r.json();
      const rows = Array.isArray(data?.data) ? data.data : [];

      // alegem primul rând unde start_time chiar diferă prev vs new
      const movedRow =
        rows.find((x: any) => {
          const prev = normalizeSnapshot(x?.prev_snapshot);
          const next = normalizeSnapshot(x?.new_snapshot);
          return (
            prev?.start_time &&
            next?.start_time &&
            prev.start_time !== next.start_time
          );
        }) || null;

      return movedRow || rows[0] || null;
    } catch (e) {
      console.error("fetchLastAuditForAppointment error:", e);
      return null;
    }
  };

  const getParentPhoneByPatient = async (patientId: string): Promise<string | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/patient/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      return data?.parent_phone || null;
    } catch {
      return null;
    }
  };

  const sendSMS = async (recipientNumber: string, messageBody: string) => {
    try {
      const r = await fetch(`${API_BASE_URL}/sms/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ to: recipientNumber, body: messageBody }),
      });

      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "SMS failed");

      Swal.fire("Succes", "SMS trimis cu succes!", "success");
    } catch (err) {
      console.error("SMS error:", err);
      Swal.fire("Eroare", "A apărut o eroare la trimiterea SMS-ului", "error");
    }
  };

    const getAppointmentLabelForSMS = (app: AppointmentProps) => {
    if (app.type === "therapy") return getTherapyTypeValue(app.appointment_description);
    if (app.type === "activity") return getActivityValue(app.appointment_description);
    if (app.type === "medical_activity") return getMedActivityValue(app.appointment_description);
    if (app.type === "medical_consult") return getMedConsultValue(app.appointment_description);
    return app.appointment_description;
  };

  const handleNotificareProgram = async (app: AppointmentProps) => {
    const phone = await getParentPhoneByPatient(app.patient_id);
    if (!phone) {
      return Swal.fire("Eroare", "Nu s-a găsit numărul de telefon al părintelui.", "error");
    }

    const dateObj = new Date(app.start_time);

    const ziua = dateObj.toLocaleDateString("ro-RO", { weekday: "long" });
    const ziuaCapitalized = ziua.charAt(0).toUpperCase() + ziua.slice(1);

    const data = dateObj.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const ora = app.start_time.slice(11, 16);

    const denumire = getAppointmentLabelForSMS(app);

    const msg =
      `Vă reamintim că ${ziuaCapitalized}, în data de ${data}, ora ${ora} ` +
      `aveți programare la Clinica Maria Beatrice pentru ${denumire}. ` +
      `Dacă nu puteți ajunge, reprogramați la nr. de tel. 0729 456 222. ` +
      `Adresa: Alba Iulia, str. Ana Aslan, nr. 34.`;

    sendSMS(phone, msg);
  };

    const handleNotificareProgramMed = async (app: AppointmentProps) => {
    const phone = await getParentPhoneByPatient(app.patient_id);
    if (!phone) {
      return Swal.fire("Eroare", "Nu s-a găsit numărul de telefon al părintelui.", "error");
    }

    const dateObj = new Date(app.start_time);

    const ziua = dateObj.toLocaleDateString("ro-RO", { weekday: "long" });
    const ziuaCapitalized = ziua.charAt(0).toUpperCase() + ziua.slice(1);

    const data = dateObj.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const ora = app.start_time.slice(11, 16);

    const denumire = getAppointmentLabelForSMS(app);

   const isConsult = app.type === "medical_consult";

const acteMsg = isConsult
  ? `Să aveți la dvs. buletin pacient/ reprezentant legal, certificat de naștere pacient minor și acte medicale dacă există. `
  : "";

const telefon = isConsult ? "0743 893 625" : "0729 456 222";

const msg =
  `Vă reamintim că ${ziuaCapitalized}, în data de ${data}, ora ${ora} ` +
  `aveți programare la Clinica Maria Beatrice pentru ${denumire}. ` +
  acteMsg +
  `Dacă nu puteți ajunge, reprogramați la nr. de tel. ${telefon}. ` +
  `Adresa: Alba Iulia, str. Ana Aslan, nr. 34.`;

    sendSMS(phone, msg);
  };

const handleNotificareModificare = async (app: AppointmentProps) => {
  const phone = await getParentPhoneByPatient(app.patient_id);
  if (!phone) {
    return Swal.fire("Eroare", "Nu s-a găsit numărul de telefon al părintelui.", "error");
  }

  // luăm audit-ul ca să aflăm ora veche + ora nouă
  const audit = await fetchLastAuditForAppointment(app.id);

  const prev = normalizeSnapshot(audit?.prev_snapshot);
  const next = normalizeSnapshot(audit?.new_snapshot);

  // fallback-uri safe (în caz că audit-ul nu vine corect)
  const oldStartISO: string | null = prev?.start_time || null;
  const newStartISO: string = next?.start_time || app.start_time;

  const data = new Date(newStartISO).toLocaleDateString("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const oraVeche = oldStartISO ? oldStartISO.slice(11, 16) : null;
  const oraNoua = newStartISO ? newStartISO.slice(11, 16) : null;

  // dacă încă nu avem ora veche, măcar să știm DE CE
  if (!oraVeche) {
    console.warn("Ora veche lipsă. Audit:", audit);
    console.warn("Prev(normalized):", prev);
    console.warn("Next(normalized):", next);
  }

  const msg = oraVeche && oraNoua
    ? `Vă informăm că programarea dvs. din data de ${data}, ora ${oraVeche} a fost mutată la ora ${oraNoua}. ` +
      `Clinica Maria Beatrice, Alba Iulia, str. Ana Aslan, nr. 34.`
    : `Vă informăm că programarea dvs. din data de ${data} a fost mutată. ` +
      `Clinica Maria Beatrice, Alba Iulia, str. Ana Aslan, nr. 34.`;

  sendSMS(phone, msg);
};

  drag(divRef);

  return (
    <div
      ref={divRef}
      className="appointment-block"
      style={{ opacity: isDragging ? 0.5 : 1, cursor: "move" }}
    >
      {/* Pacientul îl arătăm sus pentru toate tipurile, ca în pagina de medici */}
      <div className="appointment-value appointment-patient">
        <span className="appointment-text">Pacient:</span> {appointment.patient_name}
      </div>

      {/* Tipul + status */}
      {isTherapy ? (
        viewMode === "scroll" ? (
          <div className="appointment-value appointment-type">
            <span className="appointment-text">Terapie:</span>{" "}
            {getTherapyTypeValue(appointment.appointment_description)}
          </div>
        ) : (
          <div className="appointment-value appointment-type appointment-short">
            <span className="appointment-text">Terapie:</span>{" "}
            {getTherapyShortName(appointment.appointment_description)}
            <div className="appointment-value appointment-status">
              <span className="appointment-text">Status:</span>
              <StatusDropdown
                statuses={filteredStatuses}
                currentStatus={appointment.status || ""}
                onChange={(newStatus) => onStatusChange(appointment.id, newStatus)}
                isAdmin={true}
              />
            </div>
          </div>
        )
      ) : (
        // activity / medical_activity / medical_consult
        viewMode === "scroll" ? (
          <div className="appointment-value appointment-type">
            <span className="appointment-text">
              {isMedActivity ? "Activitate medicală:" : isMedConsult ? "Consult medical:" : "Activitate:"}
            </span>{" "}
            {isMedActivity
              ? getMedActivityValue(appointment.appointment_description)
              : isMedConsult
              ? getMedConsultValue(appointment.appointment_description)
              : getActivityValue(appointment.appointment_description)}
          </div>
        ) : (
          <div className="appointment-value appointment-type appointment-short">
            <span className="appointment-text">
              {isMedActivity ? "Activitate medicală:" : isMedConsult ? "Consult medical:" : "Activitate:"}
            </span>{" "}
            {isMedActivity
              ? getMedActivityShortName(appointment.appointment_description)
              : isMedConsult
              ? getMedConsultShortName(appointment.appointment_description)
              : getActivityShortName(appointment.appointment_description)}
            <div className="appointment-value appointment-status status-short">
              <span className="appointment-text">Status:</span>
              <StatusDropdown
                statuses={filteredStatuses}
                currentStatus={appointment.status || ""}
                onChange={(newStatus) => onStatusChange(appointment.id, newStatus)}
                viewMode={viewMode}
                isAdmin={true}
              />
            </div>
          </div>
        )
      )}

      {viewMode === "scroll" ? (
        <div className="appointment-value appointment-status">
          <span className="appointment-text">Status:</span>
          <StatusDropdown
            statuses={filteredStatuses}
            currentStatus={appointment.status || ""}
            onChange={(newStatus) => onStatusChange(appointment.id, newStatus)}
            viewMode={viewMode}
            isAdmin={true}
          />
        </div>
      ) : null}

      <div className="setting-dropdown-wrapper">
        <button
          className="setting-button"
          onClick={(e) => {
            e.stopPropagation();
            setDropdownOpen((v) => !v);
          }}
        >
          <img src={setting} alt="setări" />
        </button>
        {dropdownOpen && (
          <div className="dropdown-menu" ref={dropdownRef}>
            <button onClick={() => deleteAppointment(appointment.id)}>
              <img src={trash} alt="sterge" className="delete-icon" /> Șterge
            </button>
            <button onClick={() => onEdit(appointment)}>
              <img src={edit} alt="edit" className="edit-icon" /> Editează
            </button>
            <button onClick={() => onOpenHistory(appointment.id)}>
              <img src={clock} alt="history" className="history-icon" /> Istoric
            </button>

            {appointment.status === "alocat" && appointment.type === "therapy" && (
              <button onClick={() => handleNotificareProgram(appointment)}>
                <img src={phone} alt="notificare" className="notify-icon" /> Notifică părinte
              </button>
            )}
            {appointment.status === "mutat" && appointment.type === "therapy" && (
              <button onClick={() => handleNotificareModificare(appointment)}>
                <img src={phone} alt="notificare" className="notify-icon" /> Notifică părinte
              </button>
            )}
            {appointment.status === "alocat" && appointment.type === "medical_consult" && (
              <button onClick={() => handleNotificareProgramMed(appointment)}>
                <img src={phone} alt="notificare" className="notify-icon" /> Notifică părinte
              </button>
            )}
            {appointment.status === "mutat" && appointment.type === "medical_consult" && (
              <button onClick={() => handleNotificareModificare(appointment)}>
                <img src={phone} alt="notificare" className="notify-icon" /> Notifică părinte
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------- CELL -------------------- */
const AppointmentCell: React.FC<AppointmentCellProps> = ({
  appointments,
  therapistId,
  interval,
  onAddAppointment,
  onDropAppointment,
  getTherapyTypeValue,
  getActivityValue,
  getMedActivityValue,
  getMedConsultValue,
  getTherapyShortName,
  getActivityShortName,
  getMedActivityShortName,
  getMedConsultShortName,
  viewMode,
  deleteAppointment,
  statuses,
  onStatusChange,
  onEditAppointment,
  cellBackground,
  canUseCell,
  onOpenHistory,
  isMedicId,
}) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const allowed = canUseCell(therapistId, interval);

  const [, drop] = useDrop<AppointmentProps>({
    accept: ItemTypes.APPOINTMENT,
    canDrop: () => allowed && appointments.length < 10,
    drop: (draggedItem) => {
      if (allowed && appointments.length < 10) {
        onDropAppointment(draggedItem, therapistId, interval);
      }
    },
  });

  drop(cellRef);

  return (
    <div
      ref={cellRef}
      className={`appointment-cell ${allowed ? "" : "cell-disabled"}`}
      style={{ backgroundColor: cellBackground, position: "relative" }}
      onClick={() => {
        if (allowed && appointments.length === 0) {
          onAddAppointment(therapistId, interval);
        }
      }}
      title={allowed ? "" : "În afara programului de lucru"}
    >
      <div className="appointment-cell-content">
        {appointments.map((appointment, index) => (
          <div key={appointment.id} className={index > 0 ? "appointment-block-wrapper stacked" : "appointment-block-wrapper"}>
            <DraggableAppointment
              key={appointment.id}
              appointment={appointment}
              statuses={statuses}
              getTherapyTypeValue={getTherapyTypeValue}
              getActivityValue={getActivityValue}
              getMedActivityValue={getMedActivityValue}
              getMedConsultValue={getMedConsultValue}
              onStatusChange={onStatusChange}
              deleteAppointment={deleteAppointment}
              onEdit={onEditAppointment}
              getTherapyShortName={getTherapyShortName}
              getActivityShortName={getActivityShortName}
              getMedActivityShortName={getMedActivityShortName}
              getMedConsultShortName={getMedConsultShortName}
              viewMode={viewMode}
              onOpenHistory={onOpenHistory}
            />
          </div>
        ))}

        {allowed && appointments.length === 0 && (
          <button
            className="add-appointment"
            onClick={(e) => {
              e.stopPropagation();
              onAddAppointment(therapistId, interval);
            }}
          >
            Adaugă programare
          </button>
        )}

        {allowed && appointments.length >= 1 && appointments.length < 10 && (

          <button
            className="add-appointment-plus"
            onClick={(e) => {
              e.stopPropagation();
              onAddAppointment(therapistId, interval);
            }}
          >
            <img src={plusCircle} alt="adauga" className="add-new-icon" />
          </button>
        )}
      </div>
    </div>
  );
};

/* -------------------- AUTOSCROLLER -------------------- */
const AutoScroller: React.FC<{ scrollContainerRef: React.RefObject<HTMLDivElement | null> }> = ({ scrollContainerRef }) => {
  const dragDropManager = useDragDropManager();
  useEffect(() => {
    const interval = 20;
    const margin = 80;
    const speed = 15;
    const tick = () => {
      const monitor = dragDropManager.getMonitor();
      if (!monitor.isDragging()) return;
      const point = monitor.getClientOffset();
      const container = scrollContainerRef.current;
      if (!point || !container) return;
      const { top, bottom, left, right } = container.getBoundingClientRect();
      if (point.x < left + margin) container.scrollLeft -= speed;
      else if (point.x > right - margin) container.scrollLeft += speed;
      if (point.y < top + margin) container.scrollTop -= speed;
      else if (point.y > bottom - margin) container.scrollTop += speed;
      const winMargin = 60;
      if (point.y < winMargin && window.scrollY) window.scrollBy(0, -speed);
      else if (point.y > window.innerHeight - winMargin) window.scrollBy(0, speed);
    };
    const id = setInterval(tick, interval);
    return () => clearInterval(id);
  }, [dragDropManager, scrollContainerRef]);
  return null;
};

/* -------------------- PAGE -------------------- */
const AppointmentsTable: React.FC = () => {
  const { token } = useAuth();

  // date
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [medics, setMedics] = useState<Medic[]>([]);
  const [timeIntervals, setTimeIntervals] = useState<TimeInterval[]>([]);
  const [appointments, setAppointments] = useState<AppointmentProps[]>([]);
  const [activities, setActivities] = useState<any[]>([]);       // public (active)
  const [therapyTypes, setTherapyTypes] = useState<any[]>([]);   // public (active)
  const [appointmentStatuses, setAppointmentStatuses] = useState<Status[]>([]);
  const [categories, setCategories] = useState<TherapistCategory[]>([]);
  const [medicalActivities, setMedicalActivities] = useState<any[]>([]);
  const [medicalConsults, setMedicalConsults] = useState<any[]>([]);
  const [addPatientModalOpen, setAddPatientModalOpen] = useState(false);
  const [patientTargetForm, setPatientTargetForm] = useState<"add" | "edit">("add");


  const [selectedDate, setSelectedDate] = useState(formatDateToYMD(new Date()));
  const [viewMode, setViewMode] = useState<"scroll" | "full">("scroll");

  // denumiri INCL. inactive (map) pentru fallback
  const [displayTherapyMap, setDisplayTherapyMap] = useState<Record<string, { value?: string; short?: string }>>({});
  const [displayActivityMap, setDisplayActivityMap] = useState<Record<string, { value?: string; short?: string }>>({});

  // program de lucru (per user — terapeut/medic — pentru ziua selectată)
  const [allowedMap, setAllowedMap] = useState<Record<string, Array<{ start: string; end: string }>>>({});

  // Modal & search
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [copyModalIsOpen, setCopyModalIsOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [sourceDate, setSourceDate] = useState("");
  const [destinationDate, setDestinationDate] = useState("");

  const [formData, setFormData] = useState({
    therapist_id: "",
    patient_id: "",
    type: "" as "" | "therapy" | "activity" | "medical_activity" | "medical_consult",
    activity_description: "",
    therapy_type: "",
    medical_activity: "",
    medical_consult: "",
    status: "alocat",
    start_time: "",
    end_time: "",
  });

  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: "",
    therapist_id: "",
    patient_id: "",
    type: "" as "" | "therapy" | "activity" | "medical_activity" | "medical_consult",
    activity_description: "",
    therapy_type: "",
    medical_activity: "",
    medical_consult: "",
    status: "",
    start_time: "",
    end_time: "",
  });

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyApptId, setHistoryApptId] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [editSearchQuery, setEditSearchQuery] = useState("");
  const [editSearchResults, setEditSearchResults] = useState<any[]>([]);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  const isMedicId = (id: string) => medics.some((m) => m.user_id === id);

  function formatDateToYMD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /* --------- FETCH --------- */
  // Terapeuți
  useEffect(() => {
    const fetchTherapists = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/users/therapists`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        setTherapists(Array.isArray(data) ? data : data?.data || []);
      } catch (e) {
        console.error("Eroare terapeuți:", e);
      }
    };
    if (token) fetchTherapists();
  }, [token]);

  // Medici
  useEffect(() => {
    const fetchMedics = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/users/medics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        setMedics(Array.isArray(data) ? data : data?.data || []);
      } catch (e) {
        console.error("Eroare medici:", e);
      }
    };
    if (token) fetchMedics();
  }, [token]);

  // Intervale orare
  useEffect(() => {
    const fetchTimeIntervals = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/types/time-intervals?date=${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        setTimeIntervals(data.data || []);
      } catch (e) {
        console.error("Eroare intervale:", e);
      }
    };
    if (token && selectedDate) fetchTimeIntervals();
  }, [token, selectedDate]);

  // Programări
  const fetchAppointments = async (signal?: AbortSignal) => {
    try {
      const r = await fetch(`${API_BASE_URL}/appointments/get-appointments?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      const data = await r.json();
      setAppointments(data.data || []);
    } catch (e) {
      if ((e as any)?.name !== "AbortError") {
        console.error("Eroare programări:", e);
      }
    }
  };
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetchAppointments(controller.signal);
    return () => controller.abort();
  }, [token, selectedDate]);

  // Dropdown-uri ACTIVE
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [activitiesRes, therapyRes, statusRes, medActRes, medConsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/types/activities`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/therapy-type`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/appointment-status`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/medical-activities`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_BASE_URL}/types/consult-types`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);
        const [activitiesData, therapyData, statusData] = await Promise.all([
          activitiesRes.json(),
          therapyRes.json(),
          statusRes.json(),
        ]);
        setActivities(activitiesData.data || []);
        setTherapyTypes(therapyData.data || []);
        setAppointmentStatuses(statusData.data || []);
        if (medActRes) {
          const d = await medActRes.json();
          setMedicalActivities(d.data || []);
        }
        if (medConsRes) {
          const d = await medConsRes.json();
          setMedicalConsults(d.data || []);
        }
      } catch (e) {
        console.error("Eroare dropdowns:", e);
      }
    };
    if (token) fetchDropdownData();
  }, [token]);

  // Hărți de afișare (INCLUDE inactive) — din admin
  useEffect(() => {
    const fetchDisplayMaps = async () => {
      try {
        const [therapyAdminRes, activityAdminRes] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/types/therapy-type`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/admin/types/activities`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const [therapyAdmin, activityAdmin] = await Promise.all([
          therapyAdminRes.json(),
          activityAdminRes.json(),
        ]);
        const tMap: Record<string, { value?: string; short?: string }> = {};
        (Array.isArray(therapyAdmin) ? therapyAdmin : therapyAdmin?.data || []).forEach((t: any) => {
          tMap[t.code ?? t.key] = { value: t.value, short: t.short_name };
        });
        const aMap: Record<string, { value?: string; short?: string }> = {};
        (Array.isArray(activityAdmin) ? activityAdmin : activityAdmin?.data || []).forEach((a: any) => {
          aMap[a.code ?? a.key] = { value: a.value, short: a.short_name };
        });
        setDisplayTherapyMap(tMap);
        setDisplayActivityMap(aMap);
      } catch (e) {
        console.warn("Nu am putut încărca map-urile de afișare (admin).", e);
      }
    };
    if (token) fetchDisplayMaps();
  }, [token]);

  // Categorii terapeuți (și pentru medici vom folosi cheia "medical" dacă există)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/types/therapist-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        setCategories(data.data || []);
      } catch (e) {
        console.error("Eroare categorii:", e);
      }
    };
    if (token) fetchCategories();
  }, [token]);

  // Program de lucru per user (terapeut + medic)
  useEffect(() => {
    type Slot = { start: string; end: string };
    const fetchAllowed = async () => {
      const therapistIds = therapists.map((t) => t.user_id);
      const medicIds = medics.map((m) => m.user_id);
      const ids = [...therapistIds, ...medicIds];
      if (!ids.length) return;

      const entries: Array<[string, Slot[]]> = await Promise.all(
        ids.map(async (id) => {
          try {
            const r = await fetch(`${API_BASE_URL}/work-schedule/${id}/day/${selectedDate}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = await r.json();
            const arr: Slot[] = (data?.data as Slot[]) || [];
            return [id, arr];
          } catch {
            return [id, [] as Slot[]];
          }
        })
      );

      const map: Record<string, Slot[]> = {};
      entries.forEach(([id, arr]) => {
        map[id] = [...arr];
      });
      setAllowedMap(map);
    };

    if (token && selectedDate) fetchAllowed();
  }, [token, selectedDate, therapists, medics]);

  /* --------- HELPERS AFIȘARE --------- */
  const getTherapyTypeValue = (key: string) =>
    therapyTypes.find((i: any) => i.key === key)?.value || displayTherapyMap[key]?.value || key;

  const getActivityValue = (key: string) =>
    activities.find((i: any) => i.key === key)?.value || displayActivityMap[key]?.value || key;

  const getTherapyShortName = (key: string) =>
    therapyTypes.find((i: any) => i.key === key)?.short_name || displayTherapyMap[key]?.short || key;

  const getActivityShortName = (key: string) =>
    activities.find((i: any) => i.key === key)?.short_name || displayActivityMap[key]?.short || key;

  const getMedActivityValue = (key: string) =>
    medicalActivities.find((i: any) => i.key === key)?.value || key;

  const getMedConsultValue = (key: string) =>
    medicalConsults.find((i: any) => i.key === key)?.value || key;

  const getMedActivityShortName = (key: string) =>
    medicalActivities.find((i: any) => i.key === key)?.short_name || key;

  const getMedConsultShortName = (key: string) =>
    medicalConsults.find((i: any) => i.key === key)?.short_name || key;

  // Status helpers (valorile/bulele le gestionezi în CSS; aici ai doar cheia)
  const getStatusShortName = (key: string) => {
    const st = appointmentStatuses.find((s) => s.key === key);
    return st?.short_name || key;
  };

  // Verifică dacă o celulă este în program
  const canUseCell = (therapistId: string, interval: TimeInterval): boolean => {
    const blocks = allowedMap[therapistId] || [];
    return blocks.some((b) => b.start <= interval.start && b.end >= interval.end);
  };

  const handleOpenHistory = (id: string) => {
    setHistoryApptId(id);
    setHistoryOpen(true);
  };

  /* --------- ADD / DROP / EDIT / DELETE --------- */
  const handleAddAppointment = (therapistId: string, interval: TimeInterval) => {
    if (!canUseCell(therapistId, interval)) {
      Swal.fire("În afara programului", "Nu poți adăuga programări în afara programului de lucru.", "warning");
      return;
    }
    const startDateTime = `${selectedDate}T${interval.start}:00`;
    const endDateTime = `${selectedDate}T${interval.end}:00`;
    setFormData({
      therapist_id: therapistId,
      patient_id: "",
      type: isMedicId(therapistId) ? "" : "", // tipul îl alege din select
      activity_description: "",
      therapy_type: "",
      medical_activity: "",
      medical_consult: "",
      status: "alocat",
      start_time: startDateTime,
      end_time: endDateTime,
    });
    setSearchQuery("");
    setSearchResults([]);
    setModalIsOpen(true);
  };

  const handleSearchPatients = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 3) return setSearchResults([]);
    try {
      const r = await fetch(`${API_BASE_URL}/patient/search?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setSearchResults(data.data || []);
    } catch (e) {
      console.error("Eroare căutare pacienți:", e);
    }
  };
  

  // căutare pacienți în modalul de EDIT
const handleSearchEditPatients = async (query: string) => {
  setEditSearchQuery(query);
  if (query.length < 3) {
    setEditSearchResults([]);
    return;
  }
  try {
    const r = await fetch(
      `${API_BASE_URL}/patient/search?query=${encodeURIComponent(query)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = await r.json();
    setEditSearchResults(data.data || []);
  } catch (e) {
    console.error("Eroare căutare pacienți (edit):", e);
  }
};

const handleNewPatientCreated = (patient: CreatedPatient) => {
  const fullName = `${patient.firstname} ${patient.lastname}`.trim();
 
  if (patientTargetForm === "add") {
    setFormData((prev) => ({ ...prev, patient_id: patient.id }));
    setSearchQuery(fullName);
    setSearchResults([]);
  } else {
    setEditFormData((prev) => ({ ...prev, patient_id: patient.id }));
    setEditSearchQuery(fullName);
    setEditSearchResults([]);
  }
 
  setAddPatientModalOpen(false);
};

  const handleStatusChange = async (appointmentId: string, newStatus: string) => {
    try {
      const r = await fetch(`${API_BASE_URL}/appointments/update-status/${appointmentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!r.ok) {
        const { detail } = await r.json();
        throw new Error(detail || "Eroare la actualizarea statusului.");
      }
      fetchAppointments();
    } catch (e) {
      console.error(e);
      Swal.fire("Eroare", "Eroare la actualizarea statusului.", "error");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isMed = isMedicId(formData.therapist_id);
    const finalType = formData.type;
    const finalDescription =
      finalType === "therapy"
        ? formData.therapy_type
        : finalType === "activity"
        ? formData.activity_description
        : finalType === "medical_activity"
        ? formData.medical_activity
        : formData.medical_consult;

    const body = {
      therapist_id: formData.therapist_id,
      patient_id: formData.patient_id || null,
      type: finalType,
      appointment_description: finalDescription,
      status: formData.status,
      start_time: formData.start_time,
      end_time: formData.end_time,
    };

    const Toast = Swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 2000, timerProgressBar: true });
    try {
      const r = await fetch(`${API_BASE_URL}/appointments/add-appointment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const { detail } = await r.json();
        throw new Error(detail || "Eroare la adăugarea programării.");
      }
      setModalIsOpen(false);
      Toast.fire({ icon: "success", title: "Programare adăugată cu succes!" });
      fetchAppointments();
    } catch (e) {
      console.error(e);
      Swal.fire("Eroare", isMed ? "Nu s-a putut adăuga programarea la medic." : "Nu s-a putut adăuga programarea.", "error");
    }
  };

  const handleEditAppointment = (a: AppointmentProps) => {
    setEditFormData({
      id: a.id,
      therapist_id: a.therapist_id,
      patient_id: a.patient_id,
      type: a.type,
      activity_description: a.type === "activity" ? a.appointment_description : "",
      therapy_type: a.type === "therapy" ? a.appointment_description : "",
      medical_activity: a.type === "medical_activity" ? a.appointment_description : "",
      medical_consult: a.type === "medical_consult" ? a.appointment_description : "",
      status: a.status || "",
      start_time: a.start_time,
      end_time: a.end_time,
    });
    setEditSearchQuery(a.patient_name || "");
    setEditSearchResults([]);
    setEditModalIsOpen(true);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalType = editFormData.type;
    const finalDescription =
      finalType === "therapy"
        ? editFormData.therapy_type
        : finalType === "activity"
        ? editFormData.activity_description
        : finalType === "medical_activity"
        ? editFormData.medical_activity
        : editFormData.medical_consult;

    const body = {
      therapist_id: editFormData.therapist_id,
      patient_id: editFormData.patient_id || null,
      type: finalType,
      appointment_description: finalDescription,
      status: editFormData.status || undefined,
      start_time: editFormData.start_time,
      end_time: editFormData.end_time,
    };

    const Toast = Swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 2000, timerProgressBar: true });
    try {
      const r = await fetch(`${API_BASE_URL}/appointments/${editFormData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const { detail } = await r.json();
        throw new Error(detail || "Eroare la actualizarea programării.");
      }
      setEditModalIsOpen(false);
      Toast.fire({ icon: "success", title: "Programare actualizată cu succes!" });
      fetchAppointments();
    } catch (e) {
      console.error(e);
      Swal.fire("Eroare", "Nu s-a putut salva.", "error");
    }
  };

  const handleDropAppointment = async (
    dragged: AppointmentProps,
    newTherapistId: string,
    newInterval: TimeInterval
  ) => {
    if (!canUseCell(newTherapistId, newInterval)) {
      Swal.fire("În afara programului", "Nu poți muta programări în afara programului de lucru.", "warning");
      return;
    }
    if (!dragged.id) return;

    const updated = {
      therapist_id: newTherapistId,
      patient_id: dragged.patient_id || null,
      type: dragged.type,
      appointment_description: dragged.appointment_description,
      status: "mutat",
      start_time: `${selectedDate}T${newInterval.start}:00`,
      end_time: `${selectedDate}T${newInterval.end}:00`,
    };

    try {
      const r = await fetch(`${API_BASE_URL}/appointments/${dragged.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(updated),
      });
      if (!r.ok) throw new Error("Eroare la actualizarea programării.");
      fetchAppointments();
    } catch (e) {
      console.error(e);
      Swal.fire("Eroare", "Nu s-a putut muta programarea.", "error");
    }
  };

  const handleCopyAppointments = async () => {
    if (!sourceDate || !destinationDate) {
      Swal.fire("Atenție", "Selectați atât data sursă cât și data destinație.", "warning");
      return;
    }
    const Toast = Swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000, timerProgressBar: true });
    try {
      // pentru webmaster copiem TOT (terapeuți + medici)
      const r = await fetch(`${API_BASE_URL}/appointments/copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ source_date: sourceDate, destination_date: destinationDate, scope: "all" }),
      });
      if (!r.ok) {
        const { detail } = await r.json();
        throw new Error(detail || "Eroare la copierea programărilor.");
      }
      Toast.fire({ icon: "success", title: "Programările au fost copiate!" }).then(() => window.location.reload());
      setCopyModalIsOpen(false);
    } catch (e) {
      console.error("Eroare la copiere:", e);
      Swal.fire("Eroare", "Nu s-a putut copia.", "error");
    }
  };

  const handleDeleteAllAppointments = async () => {
    const result = await Swal.fire({
      title: "Ești sigur?",
      text: `Vrei să ștergi toate programările din ${selectedDate}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6C298B",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Da, șterge",
      cancelButtonText: "Anulează",
    });
    if (!result.isConfirmed) return;
    try {
      const r = await fetch(`${API_BASE_URL}/appointments/delete-by-date/${selectedDate}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const { detail } = await r.json();
        throw new Error(detail || "Eroare la ștergere.");
      }
      Swal.fire("Succes!", "Toate programările au fost șterse.", "success");
      fetchAppointments();
    } catch (e) {
      Swal.fire("Eroare!", "A apărut o eroare la ștergere.", "error");
    }
  };

  const deleteAppointment = async (appointmentId: string) => {
    const result = await Swal.fire({
      title: "Ești sigur?",
      text: "Vrei să ștergi această programare?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6C298B",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Da, șterge",
      cancelButtonText: "Anulează",
    });
    if (!result.isConfirmed) return;
    try {
      const r = await fetch(`${API_BASE_URL}/appointments/delete/${appointmentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const { detail } = await r.json();
        throw new Error(detail || "Eroare la ștergerea programării.");
      }
      Swal.fire("Succes!", "Programarea a fost ștearsă.", "success");
      fetchAppointments();
    } catch (e) {
      Swal.fire("Eroare!", "A intervenit o eroare la ștergere.", "error");
    }
  };

  /* --------- SORTARE: terapeuți apoi medici --------- */
  const sortedRows = useMemo(() => {
    type Row = {
      id: string;
      firstname: string;
      lastname: string;
      is_active: boolean;
      categoryKey?: string | null;
      orderInCategory?: number | null;
      isMedic: boolean;
    };

    const therapistRows: Row[] = therapists.map((t) => ({
      id: t.user_id,
      firstname: t.firstname,
      lastname: t.lastname,
      is_active: !!t.is_active,
      categoryKey: t.therapist_category ?? null,
      orderInCategory: typeof t.order_in_category === "number" ? t.order_in_category : null,
      isMedic: false,
    }));

    const medicRows: Row[] = medics.map((m) => ({
      id: m.user_id,
      firstname: m.firstname,
      lastname: m.lastname,
      is_active: !!m.is_active,
      categoryKey: "medical",
      orderInCategory: 9999,
      isMedic: true,
    }));

    const filtered = (rows: Row[]) => rows.filter((r) => r.is_active);
    const all = [...filtered(therapistRows), ...filtered(medicRows)];

    const getPriority = (row: Row) => {
      if (row.isMedic) {
        const medCat = categories.find((c) => c.key === "medical");
        return medCat ? Number(medCat.priority) : 9999;
      }
      const cat = categories.find((c) => c.key === row.categoryKey);
      return cat ? Number(cat.priority) : 999;
    };

    return all.sort((a, b) => {
      const pA = getPriority(a);
      const pB = getPriority(b);
      if (pA !== pB) return pA - pB;
      const oA = (a.orderInCategory ?? 9999) as number;
      const oB = (b.orderInCategory ?? 9999) as number;
      if (oA !== oB) return oA - oB;
      const nameA = `${a.lastname} ${a.firstname}`.toLowerCase();
      const nameB = `${b.lastname} ${b.firstname}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [therapists, medics, categories, selectedDate]);

  /* --------- RENDER --------- */
  return (
    <DndProvider backend={HTML5Backend}>
      <AutoScroller scrollContainerRef={scrollContainerRef} />
      <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
        <WebmasterSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />
        <div className="page-content">
          <h1 className="page-title">Adaugă și Editează Programări</h1>
          <div className="table-top">
            <div className="page-subtitle">Programări</div>
            <div className="appointments-actions">
              <div className="copy-appointments">
                <button className="primary-btn btn-copy-appointments" onClick={() => setCopyModalIsOpen(true)}>
                  <img src={copy} alt="copiaza" /> Copiază programări
                </button>
              </div>
              <div className="export-appointments">
                <ExportSchedule startDate={selectedDate} endDate={selectedDate} />
              </div>
              <div className="delete-all-appointments">
                <button className="primary-btn btn-delete-appointments" onClick={handleDeleteAllAppointments}>
                  <img src={trash} alt="sterge" /> Șterge programările
                </button>
              </div>
              <div className="appointment-date">
                <label>Data:</label>
                <input
                  type="date"
                  min="2025-01-01"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="mode-buttons">
            <button className={viewMode === "scroll" ? "mod-active" : ""} onClick={() => setViewMode("scroll")}>
              Mod scroll
            </button>
            <button className={viewMode === "full" ? "mod-active" : ""} onClick={() => setViewMode("full")}>
              Mod complet
            </button>
          </div>

          <div className={viewMode === "scroll" ? "scrollable-container" : "full-width-container"} ref={scrollContainerRef}>
            <div className="appointments-table">
              <div className="table-header">
                <div className="therapist-header">Nume / Prenume</div>
                <div className="appointments-header-scroll">
                  {timeIntervals.map((interval) => (
                    <div key={interval.start} className="appointment-header-cell">
                      {`${interval.start} - ${interval.end}`}
                    </div>
                  ))}
                </div>
              </div>

              <div className="table-body">
                {sortedRows.map((row) => {
                  const isMed = isMedicId(row.id);
                  const catKey = isMed ? "medical" : therapists.find(t => t.user_id===row.id)?.therapist_category || undefined;
                  const cat = categories.find((c) => c.key === catKey);
                  const bgColor = cat ? hexToRGBA(cat.color, 1) : "transparent";

                  return (
                    <div key={row.id} className="appointment-row">
                      <div className="therapist-cell" style={{ backgroundColor: bgColor }}>
                        <img src={avatar} alt="user" className="user-icon" /> {row.firstname} {row.lastname}
                        {isMed && <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.8 }}>(medic)</span>}
                      </div>
                      <div className="appointments-row-scroll">
                        {timeIntervals.map((interval) => {
                          const foundAppointments = appointments.filter(
                            (app) =>
                              app.therapist_id === row.id &&
                              isSameISO(app.start_time, selectedDate, interval.start)
                          );
                          return (
                            <AppointmentCell
                              key={`${row.id}-${interval.start}`}
                              appointments={foundAppointments}
                              therapistId={row.id}
                              interval={interval}
                              selectedDate={selectedDate}
                              onAddAppointment={handleAddAppointment}
                              onDropAppointment={handleDropAppointment}
                              getTherapyTypeValue={getTherapyTypeValue}
                              getActivityValue={getActivityValue}
                              getMedActivityValue={getMedActivityValue}
                              getMedConsultValue={getMedConsultValue}
                              deleteAppointment={deleteAppointment}
                              statuses={appointmentStatuses}
                              onStatusChange={handleStatusChange}
                              onEditAppointment={handleEditAppointment}
                              getTherapyShortName={getTherapyShortName}
                              getActivityShortName={getActivityShortName}
                              getMedActivityShortName={getMedActivityShortName}
                              getMedConsultShortName={getMedConsultShortName}
                              viewMode={viewMode}
                              cellBackground={foundAppointments.length > 0 ? bgColor : undefined}
                              canUseCell={canUseCell}
                              onOpenHistory={handleOpenHistory}
                              isMedicId={isMedicId}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <AppointmentHistoryModal
            appointmentId={historyApptId}
            isOpen={historyOpen}
            onRequestClose={() => setHistoryOpen(false)}
          />

          {/* Copy modal */}
          <Modal isOpen={copyModalIsOpen} onRequestClose={() => setCopyModalIsOpen(false)}>
            <h2 className="modal-title">Copiază programări</h2>
            <div className="form-copy">
              <div className="copy-date">
                <label>Data sursă:</label>
                <input type="date" min="2025-01-01" value={sourceDate} onChange={(e) => setSourceDate(e.target.value)} />
              </div>
              <div className="copy-date">
                <label>Data destinație:</label>
                <input type="date" min="2025-01-01" value={destinationDate} onChange={(e) => setDestinationDate(e.target.value)} />
              </div>
              <div className="form-row form-actions">
                <button className="cancel-btn" onClick={() => setCopyModalIsOpen(false)} type="button">Anulează</button>
                <button onClick={handleCopyAppointments} className="primary-btn">Copiază</button>
              </div>
            </div>
          </Modal>

          {/* Add modal */}
          <Modal isOpen={modalIsOpen} onRequestClose={() => setModalIsOpen(false)}>
            <h2 className="modal-title">Adaugă programare</h2>
            <form className="add-appointmen-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <label>Tip programare:</label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      type: e.target.value as any,
                      activity_description: "",
                      therapy_type: "",
                      medical_activity: "",
                      medical_consult: "",
                      // reset pacientul dacă schimb tipul
                      patient_id: "",
                    }))
                  }
                  required
                >
                  {!isMedicId(formData.therapist_id) ? (
                    <>
                      <option value="">Selectează tipul</option>
                      <option value="activity">Activitate</option>
                      <option value="therapy">Terapie</option>
                    </>
                  ) : (
                    <>
                      <option value="">Selectează tipul</option>
                      <option value="medical_activity">Activitate medicală</option>
                      <option value="medical_consult">Consult medical</option>
                    </>
                  )}
                </select>
              </div>

              {/* Pacient obligatoriu la terapie + consult medical;
                  opțional pentru activități/activități medicale care includ "evaluare" */}
              {(formData.type === "therapy" ||
                formData.type === "medical_consult" ||
                ((formData.type === "activity" || formData.type === "medical_activity") &&
                  (formData.activity_description.toLowerCase() === "evaluare" ||
                   formData.medical_activity.toLowerCase().includes("evaluare")))) && (
                <div className="form-row autocomplete-wrapper">
                  <label>Pacient:</label>
                  <input
                    type="text"
                    placeholder="Caută pacient (nume sau CNP)"
                    value={searchQuery}
                    onChange={(e) => handleSearchPatients(e.target.value)}
                    required={formData.type === "therapy" || formData.type === "medical_consult"}
                  />
                  <button
                    type="button"
                    className="add-patient-inline-btn"
                    onClick={() => {
                      setPatientTargetForm("add");
                      setAddPatientModalOpen(true);
                    }}
                    title="Adaugă pacient nou"
                  >
                    + Pacient nou
                  </button>
                  {searchResults.length > 0 && (
                    <ul className="autocomplete-list" style={{ border: "1px solid #ccc" }}>
                      {searchResults.map((patient: any) => (
                        <li
                          key={patient.id}
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, patient_id: String(patient.id) }));
                            setSearchQuery(`${patient.firstname} ${patient.lastname}`);
                            setSearchResults([]);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {patient.firstname} {patient.lastname} - {patient.cnp}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Terapeuți */}
              {formData.type === "activity" && !isMedicId(formData.therapist_id) && (
                <div className="form-row">
                  <label>Activitate:</label>
                  <select
                    value={formData.activity_description}
                    onChange={(e) => setFormData((p) => ({ ...p, activity_description: e.target.value }))}
                    required
                  >
                    <option value="">Selectează activitatea</option>
                    {activities.map((a: any) => (
                      <option key={a.key} value={a.key}>{a.value}</option>
                    ))}
                  </select>
                </div>
              )}
              {formData.type === "therapy" && !isMedicId(formData.therapist_id) && (
                <div className="form-row">
                  <label>Terapie:</label>
                  <select
                    value={formData.therapy_type}
                    onChange={(e) => setFormData((p) => ({ ...p, therapy_type: e.target.value }))}
                    required
                  >
                    <option value="">Selectează terapia</option>
                    {therapyTypes.map((t: any) => (
                      <option key={t.key} value={t.key}>{t.value}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Medici */}
              {formData.type === "medical_activity" && isMedicId(formData.therapist_id) && (
                <div className="form-row">
                  <label>Activitate medicală:</label>
                  <select
                    value={formData.medical_activity}
                    onChange={(e) => setFormData((p) => ({ ...p, medical_activity: e.target.value }))}
                    required
                  >
                    <option value="">Selectează activitatea</option>
                    {medicalActivities.map((a: any) => (
                      <option key={a.key} value={a.key}>{a.value}</option>
                    ))}
                  </select>
                </div>
              )}
              {formData.type === "medical_consult" && isMedicId(formData.therapist_id) && (
                <div className="form-row">
                  <label>Consult medical:</label>
                  <select
                    value={formData.medical_consult}
                    onChange={(e) => setFormData((p) => ({ ...p, medical_consult: e.target.value }))}
                    required
                  >
                    <option value="">Selectează tipul</option>
                    {medicalConsults.map((c: any) => (
                      <option key={c.key} value={c.key}>{c.value}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row form-actions">
                <button className="cancel-btn" onClick={() => setModalIsOpen(false)} type="button">Anulează</button>
                <button className="primary-btn" type="submit">Adaugă programare</button>
              </div>
            </form>
          </Modal>

          {/* Edit modal */}
          <Modal isOpen={editModalIsOpen} onRequestClose={() => setEditModalIsOpen(false)}>
            <h2 className="modal-title">Editează programare</h2>
            <form className="edit-appointment-form" onSubmit={handleSaveEdit}>
              <div className="form-row">
                <label>Tip programare:</label>
                <select
                  value={editFormData.type}
                  onChange={(e) =>
                    setEditFormData((p) => ({
                      ...p,
                      type: e.target.value as any,
                      activity_description: "",
                      therapy_type: "",
                      medical_activity: "",
                      medical_consult: "",
                      patient_id: "",
                    }))
                  }
                  required
                >
                  {!isMedicId(editFormData.therapist_id) ? (
                    <>
                      <option value="">Selectează tipul</option>
                      <option value="activity">Activitate</option>
                      <option value="therapy">Terapie</option>
                    </>
                  ) : (
                    <>
                      <option value="">Selectează tipul</option>
                      <option value="medical_activity">Activitate medicală</option>
                      <option value="medical_consult">Consult medical</option>
                    </>
                  )}
                </select>
              </div>

              {(editFormData.type === "therapy" ||
                editFormData.type === "medical_consult" ||
                ((editFormData.type === "activity" || editFormData.type === "medical_activity") &&
                  ((editFormData.activity_description || "").toLowerCase() === "evaluare" ||
                   (editFormData.medical_activity || "").toLowerCase().includes("evaluare")))) && (
                <div className="form-row autocomplete-wrapper">
                  <label>Pacient:</label>
                  <input
                    type="text"
                    placeholder="Caută pacient (nume sau CNP)"
                    value={editSearchQuery}
                    onChange={(e) => {
                      setEditSearchQuery(e.target.value);
                      handleSearchEditPatients(e.target.value);
                    }}
                    required={editFormData.type === "therapy" || editFormData.type === "medical_consult"}
                  />
                  <button
                    type="button"
                    className="add-patient-inline-btn"
                    onClick={() => {
                      setPatientTargetForm("edit");
                      setAddPatientModalOpen(true);
                    }}
                    title="Adaugă pacient nou"
                  >
                    + Pacient nou
                  </button>
                  {editSearchResults.length > 0 && (
                    <ul className="autocomplete-list" style={{ border: "1px solid #ccc" }}>
                      {editSearchResults.map((patient: any) => (
                        <li
                          key={patient.id}
                          onClick={() => {
                            setEditFormData((prev) => ({ ...prev, patient_id: String(patient.id) }));
                            setEditSearchQuery(`${patient.firstname} ${patient.lastname}`);
                            setEditSearchResults([]);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {patient.firstname} {patient.lastname} - {patient.cnp}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Terapeuți */}
              {editFormData.type === "activity" && !isMedicId(editFormData.therapist_id) && (
                <div className="form-row">
                  <label>Activitate:</label>
                  <select
                    value={editFormData.activity_description}
                    onChange={(e) => setEditFormData((p) => ({ ...p, activity_description: e.target.value }))}
                    required
                  >
                    <option value="">Selectează activitatea</option>
                    {activities.map((a: any) => (
                      <option key={a.key} value={a.key}>{a.value}</option>
                    ))}
                  </select>
                </div>
              )}
              {editFormData.type === "therapy" && !isMedicId(editFormData.therapist_id) && (
                <div className="form-row">
                  <label>Terapie:</label>
                  <select
                    value={editFormData.therapy_type}
                    onChange={(e) => setEditFormData((p) => ({ ...p, therapy_type: e.target.value }))}
                    required
                  >
                    <option value="">Selectează terapia</option>
                    {therapyTypes.map((t: any) => (
                      <option key={t.key} value={t.key}>{t.value}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Medici */}
              {editFormData.type === "medical_activity" && isMedicId(editFormData.therapist_id) && (
                <div className="form-row">
                  <label>Activitate medicală:</label>
                  <select
                                        value={editFormData.medical_activity}
                    onChange={(e) => setEditFormData((p) => ({ ...p, medical_activity: e.target.value }))}
                    required
                  >
                    <option value="">Selectează activitatea</option>
                    {medicalActivities.map((a: any) => (
                      <option key={a.key} value={a.key}>
                        {a.value}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editFormData.type === "medical_consult" && isMedicId(editFormData.therapist_id) && (
                <div className="form-row">
                  <label>Consult medical:</label>
                  <select
                    value={editFormData.medical_consult}
                    onChange={(e) => setEditFormData((p) => ({ ...p, medical_consult: e.target.value }))}
                    required
                  >
                    <option value="">Selectează tipul</option>
                    {medicalConsults.map((c: any) => (
                      <option key={c.key} value={c.key}>
                        {c.value}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row form-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setEditModalIsOpen(false)}
                  type="button"
                >
                  Anulează
                </button>
                <button className="primary-btn" type="submit">
                  Salvează
                </button>
              </div>
            </form>
          </Modal>
          <Modal
            isOpen={addPatientModalOpen}
            onRequestClose={() => setAddPatientModalOpen(false)}
            shouldCloseOnOverlayClick={false}
            className="patient-form-modal"            // ← clasa nouă pt sizing
          >
            <h2 className="modal-title">Adaugă pacient nou</h2>
            <PatientForm
              layout="sectioned"                       // ← AICI
              onCancel={() => setAddPatientModalOpen(false)}
              onSuccess={handleNewPatientCreated}
            />
          </Modal>
        </div>
      </div>
    </DndProvider>
  );
};

export default AppointmentsTable;